import { clarity } from "react-microsoft-clarity";
import AnalyticsService from "services/AnalyticsService";
import { API_BASE_URL } from "configs/AppConfig";
import { auth } from "auth/FirebaseAuth";
import { faIdentify, faTrack } from "services/FirebaseAnalyticsService";
import { humanizeDuration, resolveViewKey } from "utils/tracking";

export { resolveViewKey } from "utils/tracking";

/**
 * UsageTrackingService — telemetria de uso e qualidade da dashboard.
 *
 * Centraliza:
 *  - Identificação do usuário no Microsoft Clarity (identify + tags de cargo/perfil).
 *  - Eventos de sessão, page-view, toasts, falhas/lentidão de API e erros de JS,
 *    enviados via o endpoint de log existente (sendGenericLog → Datadog) e
 *    espelhados no Firebase Analytics (GA4) para funis/retenção.
 *  - A FILA ÚNICA de envio de logs genéricos (enqueueGenericLog): o endpoint
 *    grava em arquivo e não é seguro para concorrência, então TODOS os emissores
 *    do frontend (este serviço e o interceptor de requisições) passam por aqui.
 *
 * Observações:
 *  - Envio só ocorre fora de "development", espelhando o interceptor.
 *  - Todo o projeto usa um único `type` (LOG_TYPE); o evento vai em `subtype`.
 *  - Eventos deste serviço são leves (quem/o quê/quando), sem payload sensível.
 */

const GAME_ID = "global_prod";
// Facets comuns a todos os eventos de usabilidade no Datadog.
const LOG_TYPE = "DASHBOARD";
const SOURCE = "dashboard-frontend";
const isTrackingEnabled = process.env.NODE_ENV !== "development";

// --- Estado de sessão (escopo de carregamento de página / aba) -------------
let sessionId: string | null = null;
let sessionStartedAt: number | null = null;
let identifiedEmail: string | null = null;
let currentRoleNames: string[] = [];

/** Id da sessão atual (para correlacionar eventos). */
export function getSessionId(): string | null {
  return sessionId;
}

/** Cargos do usuário logado (definidos no identify). */
export function getRoleNames(): string[] {
  return currentRoleNames;
}

// --- Token para o flush keepalive no fechamento da aba ----------------------
// Atualizado pelo interceptor de requisições a cada request; permite enviar o
// SESSION_END via fetch(keepalive) quando o XHR normal já não completaria.
let lastKnownToken: string | null = null;

export function notifyAuthToken(token: string) {
  if (token) lastKnownToken = token;
}

function currentPathname(): string {
  return typeof window !== "undefined" ? window.location.pathname : "";
}

function newSessionId(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch (_) {
    /* ignore */
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function currentEmail(): string {
  try {
    return auth.currentUser?.email || identifiedEmail || "unknown";
  } catch (_) {
    return identifiedEmail || "unknown";
  }
}

// --- Fila ÚNICA e serializada de envio --------------------------------------
// O endpoint de log grava em arquivo e não é seguro para concorrência. Uma fila
// única com intervalo entre envios evita contenção em rajadas — inclusive entre
// este serviço e o auto-log de requisições (que antes tinham filas separadas e
// podiam POSTar simultaneamente).
export type GenericLogPayload = {
  gameId: string;
  logType: string;
  userEmail: string;
  customProperties: Record<string, any>;
};

const logQueue: GenericLogPayload[] = [];
let isProcessing = false;
const PROCESS_DELAY_MS = 300;

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

async function processLogQueue() {
  if (isProcessing) return;
  isProcessing = true;

  while (logQueue.length > 0) {
    const item = logQueue.shift() as GenericLogPayload;
    try {
      await AnalyticsService.sendGenericLog(item);
    } catch (err) {
      console.error("[tracking] sendGenericLog failed", err);
    }
    await delay(PROCESS_DELAY_MS);
  }

  isProcessing = false;
}

/** Enfileira um log genérico bruto. Use para logs fora da taxonomia deste serviço. */
export function enqueueGenericLog(payload: GenericLogPayload) {
  if (!isTrackingEnabled) return;
  logQueue.push(payload);
  processLogQueue();
}

/**
 * Envio direto via fetch(keepalive) — sobrevive ao unload da página, ao
 * contrário do XHR na fila. Best-effort: retorna false se não for possível.
 */
function flushViaKeepalive(payload: GenericLogPayload): boolean {
  try {
    if (typeof fetch !== "function" || !lastKnownToken) return false;
    fetch(`${API_BASE_URL}/analytics/${payload.gameId}/sendGenericLog`, {
      method: "POST",
      keepalive: true,
      headers: {
        "content-type": "application/json",
        authorization: `Bearer ${lastKnownToken}`,
      },
      body: JSON.stringify({
        logType: payload.logType,
        userEmail: payload.userEmail,
        customProperties: payload.customProperties,
      }),
    }).catch(() => {
      /* best-effort no unload */
    });
    return true;
  } catch (_) {
    return false;
  }
}

function sendLog(
  email: string,
  subtype: string,
  customProperties: Record<string, any>,
  opts?: { flush?: boolean }
) {
  if (!isTrackingEnabled) return;
  // Envelope comum para consumo unificado no Datadog. Todo evento compartilha o
  // mesmo `type` (LOG_TYPE); o discriminador do evento fica em `subtype`.
  const enriched = {
    source: SOURCE,
    env: process.env.NODE_ENV || "unknown",
    role: currentRoleNames.length ? currentRoleNames.join(", ") : "unknown",
    subtype,
    ...customProperties,
  };
  const payload: GenericLogPayload = {
    gameId: GAME_ID,
    logType: LOG_TYPE,
    userEmail: email || "unknown",
    customProperties: enriched,
  };

  // No unload da página a fila assíncrona não completa; envia via keepalive.
  if (!(opts?.flush && flushViaKeepalive(payload))) {
    logQueue.push(payload);
    processLogQueue();
  }

  // Track D: espelha o evento no product analytics (Firebase/GA4) para funis e
  // retenção. O FRONTEND_ACTION por-requisição não passa por aqui (fica só no
  // Datadog) — granular demais para product analytics.
  faTrack(subtype, enriched);
}

/**
 * Executa `fn` assim que o Clarity estiver ativo. O script do Clarity carrega de
 * forma assíncrona após clarity.init(), então tentamos por alguns segundos.
 */
function withClarity(fn: () => void) {
  let tries = 0;
  const attempt = () => {
    try {
      if (clarity.hasStarted()) {
        fn();
        return;
      }
    } catch (_) {
      /* ignore */
    }
    if (tries++ < 20) setTimeout(attempt, 500);
  };
  attempt();
}

// --- API pública -----------------------------------------------------------

/**
 * Identifica o usuário logado no Clarity e adiciona tags para segmentação
 * (cargo, quantidade de views/permissões e ambiente).
 */
export function identifyUser(
  email: string,
  roleNames: string[] = [],
  viewsCount = 0,
  permsCount = 0
) {
  currentRoleNames = roleNames;
  if (!email || identifiedEmail === email) return;
  identifiedEmail = email;

  // Track D: identifica no GA4 com o UID interno do Firebase, sem enviar e-mail/PII.
  faIdentify(auth.currentUser?.uid, roleNames);

  withClarity(() => {
    try {
      // As tipagens do pacote estão desatualizadas: identify aceita apenas o
      // userId neste wrapper e setTag aceita string[] em runtime (a API do
      // Clarity suporta), então usamos aliases tipados corretamente.
      const clarityIdentify = clarity.identify as unknown as (userId: string) => void;
      const claritySetTag = clarity.setTag as unknown as (
        key: string,
        value: string | string[]
      ) => void;

      clarityIdentify(email);
      claritySetTag("userEmail", email);
      if (roleNames.length) claritySetTag("role", roleNames);
      claritySetTag("viewsCount", String(viewsCount));
      claritySetTag("permsCount", String(permsCount));
      claritySetTag("env", process.env.NODE_ENV || "unknown");
    } catch (err) {
      console.error("[tracking] clarity identify failed", err);
    }
  });
}

/** Marca o início de uma sessão (uma vez por carregamento de página). */
export function trackSessionStart(email: string) {
  if (sessionId) return;
  sessionId = newSessionId();
  sessionStartedAt = Date.now();

  sendLog(email, "SESSION_START", {
    sessionId,
    startedAt: new Date(sessionStartedAt).toISOString(),
    userAgent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
    screen:
      typeof window !== "undefined" && window.screen
        ? `${window.screen.width}x${window.screen.height}`
        : undefined,
    referrer: typeof document !== "undefined" ? document.referrer || undefined : undefined,
  });
}

/**
 * Marca o fim da sessão (logout ou fechamento). Idempotente.
 * Com `flush: true` (unload da página), envia via fetch(keepalive).
 */
export function trackSessionEnd(email?: string, opts?: { flush?: boolean }) {
  if (!sessionId) return;
  const durationMs = sessionStartedAt ? Date.now() - sessionStartedAt : undefined;

  sendLog(
    email || identifiedEmail || "unknown",
    "SESSION_END",
    {
      sessionId,
      durationMs,
      durationReadable: durationMs != null ? humanizeDuration(durationMs) : undefined,
    },
    opts
  );

  sessionId = null;
  sessionStartedAt = null;
}

// --- Page views (com tempo de permanência na tela anterior) -----------------
let lastViewKey: string | null = null;
let lastViewStartedAt: number | null = null;

/** Registra a visualização de uma rota/feature. */
export function trackPageView(pathname: string, email?: string) {
  const viewKey = resolveViewKey(pathname);
  const now = Date.now();
  // Tempo na tela anterior: distingue tela usada de verdade de "bounce".
  const previousViewKey = lastViewKey || undefined;
  const previousViewDurationMs =
    lastViewKey && lastViewStartedAt ? now - lastViewStartedAt : undefined;
  lastViewKey = viewKey;
  lastViewStartedAt = now;

  sendLog(email || currentEmail(), "PAGE_VIEW", {
    viewKey,
    path: pathname,
    sessionId: sessionId || undefined,
    previousViewKey,
    previousViewDurationMs,
  });
}

// --- Rastreio de toasts (sucesso/falha percebidos pelo usuário) -----------
// Dedupe de curto prazo: evita duplicar toasts idênticos em re-renders/loops.
const recentToasts = new Map<string, number>();
const TOAST_DEDUPE_MS = 1500;

export type ToastOutcome = "success" | "error" | "warning" | "info";

export function trackToast(params: {
  outcome: ToastOutcome;
  channel: "notification" | "message";
  method: string;
  text?: string;
}) {
  if (!isTrackingEnabled) return;

  const pathname = currentPathname();
  const viewKey = resolveViewKey(pathname);
  const text = params.text ? String(params.text).slice(0, 300) : undefined;

  const now = Date.now();
  const dedupeKey = `${params.outcome}|${params.method}|${viewKey}|${text || ""}`;
  const last = recentToasts.get(dedupeKey);
  if (last && now - last < TOAST_DEDUPE_MS) return;
  recentToasts.set(dedupeKey, now);
  if (recentToasts.size > 200) recentToasts.clear();

  sendLog(currentEmail(), "TOAST", {
    outcome: params.outcome,
    channel: params.channel,
    method: params.method,
    text,
    viewKey,
    path: pathname,
    sessionId: sessionId || undefined,
  });
}

// --- Falhas de API (falha de entrega/acionamento, mesmo sem toast) ---------
/**
 * Registra falhas de requisição capturadas pelo interceptor de resposta.
 * Captura inclusive falhas silenciosas (timeout, network error) que não geram
 * toast. NÃO deve ser chamado para as rotas de log (evita loop de tracking).
 */
export function trackApiError(params: {
  status?: number;
  httpMethod?: string;
  url?: string;
  message?: string;
  durationMs?: number;
  retries?: number;
}) {
  if (!isTrackingEnabled) return;

  const pathname = currentPathname();

  sendLog(currentEmail(), "API_ERROR", {
    status: params.status ?? null,
    httpMethod: params.httpMethod,
    url: params.url,
    message: params.message ? String(params.message).slice(0, 300) : undefined,
    durationMs: params.durationMs,
    retries: params.retries,
    isNetworkError: params.status == null,
    viewKey: resolveViewKey(pathname),
    path: pathname,
    sessionId: sessionId || undefined,
  });
}

// --- Requisições lentas (degradação percebida antes do downtime) ------------
export function trackApiSlow(params: {
  url?: string;
  httpMethod?: string;
  durationMs: number;
  status?: number;
}) {
  if (!isTrackingEnabled) return;

  const pathname = currentPathname();

  sendLog(currentEmail(), "API_SLOW", {
    url: params.url,
    httpMethod: params.httpMethod,
    durationMs: params.durationMs,
    status: params.status,
    viewKey: resolveViewKey(pathname),
    path: pathname,
    sessionId: sessionId || undefined,
  });
}

// --- Erros de JavaScript (crash de render = downtime percebido) -------------
// Dedupe mais longo (erros em loop/interval geram storm) + teto por sessão.
const recentJsErrors = new Map<string, number>();
const JS_ERROR_DEDUPE_MS = 30000;
const MAX_JS_ERRORS_PER_SESSION = 30;
let jsErrorCount = 0;

export function trackJsError(params: {
  message?: string;
  stack?: string;
  componentStack?: string;
  origin: "window.onerror" | "unhandledrejection" | "react-error-boundary";
}) {
  if (!isTrackingEnabled) return;
  if (jsErrorCount >= MAX_JS_ERRORS_PER_SESSION) return;

  const message = params.message ? String(params.message).slice(0, 300) : "unknown";
  const now = Date.now();
  const dedupeKey = `${params.origin}|${message}`;
  const last = recentJsErrors.get(dedupeKey);
  if (last && now - last < JS_ERROR_DEDUPE_MS) return;
  recentJsErrors.set(dedupeKey, now);
  jsErrorCount++;

  const pathname = currentPathname();

  sendLog(currentEmail(), "JS_ERROR", {
    message,
    stack: params.stack ? String(params.stack).slice(0, 1000) : undefined,
    componentStack: params.componentStack
      ? String(params.componentStack).slice(0, 1000)
      : undefined,
    origin: params.origin,
    viewKey: resolveViewKey(pathname),
    path: pathname,
    sessionId: sessionId || undefined,
  });
}

let globalErrorTrackingInitialized = false;

/** Registra listeners globais de erro (exceções não tratadas e promises rejeitadas). */
export function initGlobalErrorTracking() {
  if (globalErrorTrackingInitialized || typeof window === "undefined") return;
  globalErrorTrackingInitialized = true;

  window.addEventListener("error", (event: ErrorEvent) => {
    trackJsError({
      message: event?.message || String(event?.error || "unknown"),
      stack: event?.error?.stack,
      origin: "window.onerror",
    });
  });

  window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
    const reason: any = event?.reason;
    trackJsError({
      message: reason?.message || String(reason ?? "unknown"),
      stack: reason?.stack,
      origin: "unhandledrejection",
    });
  });
}
